// ─── /design — the living style page ────────────────────────────────────────
// Haley, 2026-09-14: "help me update everything so it looks better." Every
// surface in the app reads the same token layer (theme.js buildTokens) and
// the same shared style objects (SixGuysRP.jsx: btnPrimary, inputStyle,
// topBar, modalContent…). This page renders that shared vocabulary from the
// LIVE tokens — nav, cards, buttons, inputs, message bubbles, sidebar rows,
// chips, a modal — in every theme and at phone width, so a refresh can be
// seen and judged without opening a real story. No data, no auth: it is
// only the vocabulary. Reachable at /design.
import { useContext, useEffect, useState } from 'react';
import { ThemeContext } from './ThemeContext.jsx';
import { THEMES } from './theme.js';
import { sharedStyles } from './sharedStyles.js';

const label = { fontSize: 'var(--ds-fs-xs)', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ds-ink-low)', fontFamily: 'var(--ds-font-ui)', fontWeight: 600, margin: '0 0 var(--ds-sp-3)' };
const section = { marginBottom: 'var(--ds-sp-6)' };

function Bubble({ me, name, color, children, ooc }) {
  return (
    <div className={me ? 'msg-send' : 'msg-receive'} style={{ display: 'flex', justifyContent: me ? 'flex-end' : 'flex-start', marginBottom: '6px' }}>
      <div className={me ? 'rp-msg rp-msg-me' : 'rp-msg rp-msg-them'} style={{ maxWidth: '70%', padding: '10px 14px', borderRadius: me ? '18px 18px 4px 18px' : '18px 18px 18px 4px', background: ooc ? 'rgba(250,166,26,0.15)' : (me ? (color || 'var(--ds-gold)') : 'var(--ds-hover)'), color: me && !ooc ? 'var(--ds-on-gold)' : 'var(--ds-ink-hi)', fontSize: '14px', position: 'relative' }}>
        {!me && <div style={{ fontSize: '11px', color: color || 'var(--ds-gold)', fontWeight: 700, marginBottom: '3px' }}>{name}</div>}
        {ooc && <span style={{ fontSize: '9px', background: 'var(--ds-gold-a33)', color: 'var(--ds-gold)', padding: '1px 5px', borderRadius: '6px', marginBottom: '4px', display: 'inline-block' }}>OOC</span>}
        <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'var(--ds-font-body)', fontSize: 'var(--ds-fs-md)', lineHeight: 1.6, fontStyle: ooc ? 'italic' : 'normal' }}>{children}</div>
        <div style={{ fontSize: '9px', opacity: 0.55, marginTop: '4px', textAlign: me ? 'right' : 'left' }}>Sep 14, 2026, 3:12 PM EDT</div>
      </div>
    </div>
  );
}

export default function DesignShowcase() {
  const { theme: t, themeId, setThemeId } = useContext(ThemeContext);
  const [open, setOpen] = useState(false);
  const [baseSize, setBaseSize] = useState('');
  const S = sharedStyles;
  // ?theme=porcelain deep-links a theme (screenshots, sharing a look); the
  // tokens land on <html> in ThemeProvider's effect, so read them after it.
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('theme');
      if (q && THEMES[q] && q !== themeId) setThemeId(q);
    } catch { /* no URL access — keep the stored theme */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    try { setBaseSize(getComputedStyle(document.documentElement).getPropertyValue('--ds-fs-base').trim()); } catch { /* SSR-safe */ }
  }, [themeId]);
  return (
    <div style={{ minHeight: '100vh', background: 'var(--ds-surface-0)', color: 'var(--ds-ink-hi)', fontFamily: 'var(--ds-font-body)' }}>
      <div className="main-nav" style={{ display: 'flex', alignItems: 'center', gap: 'var(--ds-sp-3)', padding: '0 var(--ds-sp-4)', height: 52, position: 'sticky', top: 0, zIndex: 5 }}>
        <div style={{ fontFamily: 'var(--ds-font-display)', letterSpacing: '0.12em', color: 'var(--ds-gold)', fontSize: 'var(--ds-fs-md)', whiteSpace: 'nowrap' }}>MY FATE, MY LOVE</div>
        <div style={{ flex: 1 }} />
        {['RP', 'DMs', 'Our Channels', 'Stories'].map((n, i) => (
          <button key={n} className={`nav-btn ds-desktop-only${i === 0 ? ' active' : ''}`} style={{ background: i === 0 ? 'var(--ds-accent-a12)' : 'transparent', border: 'none', borderBottom: '2px solid transparent', borderTop: '2px solid transparent', color: i === 0 ? 'var(--ds-ink-hi)' : 'var(--ds-ink-low)', padding: '12px 10px', fontSize: '12px', fontWeight: i === 0 ? 700 : 600, cursor: 'pointer', fontFamily: 'var(--ds-font-display)', letterSpacing: '1.5px', textTransform: 'uppercase', position: 'relative', overflow: 'hidden', borderRadius: i === 0 ? '4px 4px 0 0' : '4px', height: 52 }}>{n}</button>
        ))}
        <span className="mf-dot" aria-hidden="true" />
        <button className="ds-phone-only" aria-label="Menu" style={{ background: 'none', border: '1px solid var(--ds-gold-a26)', borderRadius: 'var(--ds-r-sm)', color: 'var(--ds-gold)', width: 40, height: 40, fontSize: 18, cursor: 'pointer' }}>☰</button>
      </div>
      <style>{`
        .ds-phone-only { display: none; }
        @media (max-width: 640px) {
          .ds-two-col { grid-template-columns: minmax(0, 1fr) !important; }
          .ds-desktop-only { display: none !important; }
          .ds-phone-only { display: inline-flex; align-items: center; justify-content: center; }
        }
      `}</style>

      <div style={{ maxWidth: 980, margin: '0 auto', padding: 'var(--ds-sp-5) var(--ds-sp-4)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--ds-sp-2)', alignItems: 'center', marginBottom: 'var(--ds-sp-5)' }}>
          <span style={label}>Theme</span>
          {Object.keys(THEMES).map(id => (
            <button key={id} onClick={() => setThemeId(id)} style={{ ...S.genTab, ...(id === themeId ? { borderColor: 'var(--ds-gold)', boxShadow: 'var(--ds-glow-gold)' } : {}) }}>{id}</button>
          ))}
        </div>

        <div style={section}>
          <p style={label}>Type</p>
          <h1 className="bq-title" style={{ fontFamily: 'var(--ds-font-display)', color: 'var(--ds-gold)', letterSpacing: '2px', fontSize: 'var(--ds-fs-2xl)', margin: '0 0 var(--ds-sp-2)' }}>Your Stories</h1>
          <p style={{ fontSize: 'var(--ds-fs-md)', lineHeight: 1.6, maxWidth: '68ch', margin: '0 0 var(--ds-sp-2)', color: 'var(--ds-ink-hi)' }}>The brush of her lips against his pulled a low sound from him, not quite a growl, something softer and rawer underneath. His hand stayed at her hip, holding her steady the way she needed, but he didn't rush her.</p>
          <p style={{ fontSize: 'var(--ds-fs-sm)', color: 'var(--ds-ink-low)', margin: 0 }}>Secondary text · timestamps · hints — {t.light ? 'light' : 'dark'} theme · base {baseSize || '…'}</p>
        </div>

        <div style={section}>
          <p style={label}>Buttons</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--ds-sp-2)', alignItems: 'center' }}>
            <button style={S.btnPrimary}>Send</button>
            <button className="mf-btn-secondary">Regenerate</button>
            <button className="mf-btn-ghost">Cancel</button>
            <button style={S.btnSmall}>Small</button>
            <button style={S.genTab}>Chip</button>
            <button style={{ ...S.genTab, borderColor: 'var(--ds-gold)' }}>Chip · active</button>
            <button className="mf-btn-danger">Delete</button>
          </div>
        </div>

        <div style={section}>
          <p style={label}>Inputs</p>
          <div style={{ display: 'grid', gap: 'var(--ds-sp-3)', maxWidth: 520 }}>
            <input style={S.inputStyle} placeholder="Search all characters…" />
            <textarea className="input-glow" style={{ ...S.inputStyle, minHeight: 72, resize: 'none' }} placeholder="Write as Haley… (use (( )) for OOC)" />
            <select style={S.inputStyle}><option>Talking to: the scene</option></select>
          </div>
        </div>

        <div className="ds-two-col" style={{ ...section, display: 'grid', gridTemplateColumns: 'minmax(0, 260px) minmax(0, 1fr)', gap: 'var(--ds-sp-4)' }}>
          <div>
            <p style={label}>Sidebar rows</p>
            {[['🧜‍♀️', 'Kya Miyamoto', true], ['🦋', 'Kieran', false], ['✨', 'Atropos', false], ['🌟', 'Princess Layla Summerfall', false]].map(([e, n, active]) => (
              <div key={n} className={`mf-row${active ? ' mf-row-active' : ''}`}><span style={{ fontSize: 16 }}>{e}</span><span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n}</span>{active && <span className="mf-dot" />}</div>
            ))}
            <p style={{ ...label, marginTop: 'var(--ds-sp-4)' }}>Story card</p>
            <div className="story-card mf-card" style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 'var(--ds-fs-base)' }}>Haley · 8/30/2026</div>
                <span className="mf-pill">🎯 Teagan</span>
              </div>
              <div style={{ fontSize: 'var(--ds-fs-sm)', color: 'var(--ds-ink-low)', marginTop: 4 }}>Last played Sep 14 · 412 messages</div>
            </div>
          </div>
          <div>
            <p style={label}>Messages</p>
            <div style={{ background: 'var(--ds-surface-1)', borderRadius: 'var(--ds-r-md)', padding: 'var(--ds-sp-4)', border: '1px solid var(--ds-line-soft)' }}>
              <Bubble name="Teagan" color="#4a8f6e">The sound she made when his thumb found the mark went straight through him. He leaned in, close enough that his breath moved warm against her ear.</Bubble>
              <Bubble me color={t.accentGold}>"…how can I make you forget it if I'm biting my lip to stop myself from screaming it too loud?"</Bubble>
              <Bubble name="Haley" ooc>((let him take the lead this time))</Bubble>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                <textarea className="input-glow" style={{ ...S.inputStyle, flex: 1, minHeight: 44, resize: 'none', borderRadius: 'var(--ds-r-sm)' }} placeholder="Write as Haley…" />
                <button style={{ ...S.btnPrimary, padding: '12px 22px', borderRadius: 'var(--ds-r-sm)' }}>Send</button>
              </div>
            </div>
          </div>
        </div>

        <div style={section}>
          <p style={label}>Modal</p>
          <button className="mf-btn-secondary" onClick={() => setOpen(true)}>Open a modal</button>
          {open && (
            <div style={S.modalOverlay} onClick={() => setOpen(false)}>
              <div style={S.modalContent} onClick={e => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 var(--ds-sp-2)', fontFamily: 'var(--ds-font-display)', color: 'var(--ds-gold)', letterSpacing: '1px', fontSize: 'var(--ds-fs-lg)' }}>Delete this group?</h3>
                <p style={{ margin: '0 0 var(--ds-sp-4)', color: 'var(--ds-ink)' }}>This hides the group from the World for everyone. You will be asked once more.</p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="mf-btn-ghost" onClick={() => setOpen(false)}>Keep it</button>
                  <button className="mf-btn-danger" onClick={() => setOpen(false)}>Delete</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
